import React from 'react';
import { cssMap } from '@atlaskit/css';
import Skeleton from '@atlaskit/skeleton';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

const loadingMessage = defineMessage({
	id: 'servicedesk-solution-composer.rovo-agent-skeleton.loading',
	defaultMessage: 'Loading Rovo agents',
	description: 'Aria label for the Rovo agents skeleton',
});

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		minWidth: '640px',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface'),
	},
	content: {
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
	leftColumn: {
		flex: '0 0 380px',
	},
	rightColumn: {
		flex: '1',
	},
	portalCard: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
});

export const RovoAgentSkeleton = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.container}
			role="status"
			aria-busy="true"
			aria-label={formatMessage(loadingMessage)}
		>
			<Box xcss={styles.content}>
				<Inline space="space.500" alignBlock="start" shouldWrap={false}>
					<Stack xcss={styles.leftColumn} space="space.200">
						<Skeleton width="70%" height="32px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton
							width="100%"
							height="20px"
							borderRadius={token('radius.small')}
							isShimmering
						/>
						<Skeleton width="95%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton width="55%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton width="88%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton width="80%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton width="74%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Skeleton width="38%" height="20px" borderRadius={token('radius.small')} isShimmering />
						<Box xcss={styles.portalCard}>
							<Stack space="space.150">
								<Skeleton
									width="45%"
									height="18px"
									borderRadius={token('radius.small')}
									isShimmering
								/>
								<Skeleton
									width="55%"
									height="18px"
									borderRadius={token('radius.small')}
									isShimmering
								/>
								<Inline space="space.100">
									<Skeleton
										width={140}
										height={token('space.400')}
										borderRadius={token('radius.medium')}
										isShimmering
									/>
									<Skeleton
										width={160}
										height={token('space.400')}
										borderRadius={token('radius.medium')}
										isShimmering
									/>
								</Inline>
							</Stack>
						</Box>
					</Stack>
					<Box xcss={styles.rightColumn}>
						<Skeleton
							width="100%"
							height="420px"
							borderRadius={token('radius.large')}
							isShimmering
						/>
					</Box>
				</Inline>
			</Box>
		</Box>
	);
};
